import { A4, jpegPdf } from './pdf';
import { coverRect, engravingDetailLines, frameTitle, orderLines, type OrderCopy, type OrderSnapshot } from './orderSpecs';

/**
 * A201 (David : « PDF + code commande ») — the order sheet as a PDF, built on the device from the try-on:
 *   page 1 — the order: Maison Mata, the order code, the model, the date, the try-on photo, every choice
 *            (acetate with its reference, lenses, size, PD) and, with an engraving, each view in words;
 *   page 2 — for a Handmade frame, the workshop sheet: the four faces of the temples drawn as the customer saw
 *            them (`workshopPage` in the engraving module).
 * Pages are drawn on canvases (A4 at 150 dpi) and written as full-page JPEGs (`pdf.ts`).
 */
export const ORDER_PAGE = {
  width: 1240,
  height: 1754,
  margin: 84,
  photo: 600,
  quality: 0.9,
  paper: '#f6f2ec',
  card: '#ebe5dc',
  ink: '#1d1d1b',
  muted: '#5b5750',
  rule: 'rgba(29, 29, 27, .14)',
} as const;

export interface OrderPageInput {
  photo: HTMLCanvasElement | null;
  focus: { x: number; y: number } | null;
  snapshot: OrderSnapshot;
  copy: OrderCopy;
  code: string;
  url: string | null;
  family: string;
  /** « 9 octobre 2026 ». */
  date: string;
}

type Ctx = CanvasRenderingContext2D;

function spaced(ctx: Ctx, text: string, x: number, y: number, spacing: number): void {
  for (const g of Array.from(text)) { ctx.fillText(g, x, y); x += ctx.measureText(g).width + spacing; }
}

/** Lines no wider than `max` (greedy, by words; a word longer than the line is kept whole). */
export function wrapText(measure: (t: string) => number, text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (line && measure(next) > max) { out.push(line); line = word; } else line = next;
  }
  if (line) out.push(line);
  return out;
}

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Page 1. Fonts must be loaded (the caller waits for them). */
export function renderOrderPage(input: OrderPageInput): HTMLCanvasElement | null {
  const P = ORDER_PAGE;
  const { snapshot: s, copy } = input;
  const o = copy.order;
  const canvas = document.createElement('canvas');
  canvas.width = P.width; canvas.height = P.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const inner = P.width - 2 * P.margin;
  const family = input.family;
  const symbols = `'Mata Symboles', ${family}`;
  ctx.fillStyle = P.paper;
  ctx.fillRect(0, 0, P.width, P.height);
  ctx.textBaseline = 'alphabetic';

  // Head: MAISON MATA, the order code on the right.
  let y = 96;
  ctx.textAlign = 'left';
  ctx.fillStyle = P.ink;
  ctx.font = `600 22px ${family}`;
  spaced(ctx, 'MAISON MATA', P.margin, y, 6);
  ctx.textAlign = 'right';
  ctx.fillStyle = P.muted;
  ctx.font = `500 17px ${family}`;
  ctx.fillText((o.code ?? '').toUpperCase(), P.width - P.margin, y - 26);
  ctx.fillStyle = P.ink;
  ctx.font = `600 28px ${family}`;
  ctx.fillText(input.code, P.width - P.margin, y + 6);
  ctx.textAlign = 'left';
  y += 44;
  ctx.fillStyle = P.rule;
  ctx.fillRect(P.margin, y, inner, 2);

  // Title and date
  y += 66;
  ctx.fillStyle = P.ink;
  ctx.font = `600 44px ${family}`;
  ctx.fillText(frameTitle(s), P.margin, y, inner);
  y += 40;
  ctx.fillStyle = P.muted;
  ctx.font = `400 22px ${family}`;
  ctx.fillText(`${o.pdfTitle ?? ''} · ${o.pdfDate ? o.pdfDate(input.date) : input.date}`, P.margin, y, inner);
  y += 36;

  // Photo (left), choices (right)
  const top = y;
  const side = P.photo;
  ctx.save();
  roundRect(ctx, P.margin, top, side, side, 20);
  ctx.fillStyle = P.card;
  ctx.fill();
  ctx.clip();
  if (input.photo && input.photo.width && input.photo.height) {
    const crop = coverRect(input.photo.width, input.photo.height, side, side, input.focus ?? undefined);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(input.photo, crop.sx, crop.sy, crop.sw, crop.sh, P.margin, top, side, side);
  }
  ctx.restore();

  const colX = P.margin + side + 48;
  const colW = P.width - P.margin - colX;
  let cy = top + 8;
  for (const line of orderLines(s, copy, true)) {
    ctx.fillStyle = P.muted;
    ctx.font = `500 16px ${family}`;
    cy += 18;
    spaced(ctx, line.label.toUpperCase(), colX, cy, 2.5);
    ctx.fillStyle = P.ink;
    ctx.font = `400 25px ${symbols}`;
    for (const part of wrapText(t => ctx.measureText(t).width, line.value, colW)) { cy += 33; ctx.fillText(part, colX, cy, colW); }
    cy += 22;
  }

  // Engraving in words: one paragraph per view.
  y = Math.max(top + side, cy) + 52;
  const details = engravingDetailLines(s, copy);
  if (details.length && copy.engrave) {
    ctx.fillStyle = P.rule;
    ctx.fillRect(P.margin, y - 30, inner, 2);
    ctx.fillStyle = P.ink;
    ctx.font = `600 22px ${family}`;
    y += 4;
    ctx.fillText(`${copy.engrave.orderLabel}${o.separator}${s.engraving?.fontName ?? ''}`, P.margin, y, inner);
    y += 12;
    ctx.font = `400 21px ${symbols}`;
    for (const d of details) {
      for (const [i, part] of wrapText(t => ctx.measureText(t).width, d, inner - (0)).entries()) {
        y += 29;
        if (y > P.height - 120) break;
        ctx.fillStyle = i === 0 ? P.ink : P.muted;
        ctx.fillText(part, P.margin, y, inner);
      }
      y += 8;
    }
  }

  // Foot: the product page and the note.
  ctx.font = `400 18px ${family}`;
  ctx.fillStyle = P.muted;
  const foot = wrapText(t => ctx.measureText(t).width, o.pdfNote ?? '', inner);
  let fy = P.height - 56 - (foot.length - 1) * 24;
  if (input.url) ctx.fillText(input.url.replace(/^https?:\/\//, ''), P.margin, fy - 32, inner);
  for (const line of foot) { ctx.fillText(line, P.margin, fy, inner); fy += 24; }
  return canvas;
}

/** A canvas as JPEG bytes (null when the browser cannot encode it). */
export function canvasJpeg(canvas: HTMLCanvasElement, quality: number = ORDER_PAGE.quality): Promise<Uint8Array | null> {
  return new Promise(resolve => {
    try {
      canvas.toBlob(blob => {
        if (!blob) { resolve(null); return; }
        void blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf)), () => resolve(null));
      }, 'image/jpeg', quality);
    } catch {
      resolve(null);
    }
  });
}

/** The PDF of the given pages (null pages are skipped); null when no page could be encoded. */
export async function composeOrderPdf(pages: Array<HTMLCanvasElement | null>, meta: { title: string; subject: string }): Promise<Blob | null> {
  const encoded: Array<{ jpeg: Uint8Array; width: number; height: number }> = [];
  for (const page of pages) {
    if (!page) continue;
    const jpeg = await canvasJpeg(page);
    if (jpeg) encoded.push({ jpeg, width: page.width, height: page.height });
    // Released at once (two 1240 × 1754 canvases per order).
    page.width = 0; page.height = 0;
  }
  if (!encoded.length) return null;
  const bytes = jpegPdf(encoded, { title: meta.title, subject: meta.subject, author: 'Maison Mata' });
  return new Blob([bytes as BlobPart], { type: 'application/pdf' });
}

/** « maison-mata-airmax-MM-0910-K7Q4F.pdf ». */
export function pdfFileName(frameId: string, code: string): string {
  const safe = frameId.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'essayage';
  return `maison-mata-${safe}-${code}.pdf`;
}

export { A4 };
